import React, { useState } from 'react';

import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const AddRecipientModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    name: '',
    relationship: '',
    bankName: '',
    iban: '',
    swiftCode: '',
    country: '',
    currency: '',
    accountHolderName: '',
    bankAddress: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const countryOptions = [
    { value: 'United Kingdom', label: 'United Kingdom' },
    { value: 'Germany', label: 'Germany' },
    { value: 'France', label: 'France' },
    { value: 'Spain', label: 'Spain' },
    { value: 'Italy', label: 'Italy' },
    { value: 'Netherlands', label: 'Netherlands' },
    { value: 'Belgium', label: 'Belgium' },
    { value: 'Austria', label: 'Austria' },
    { value: 'Portugal', label: 'Portugal' },
    { value: 'Ireland', label: 'Ireland' }
  ];

  const relationshipOptions = [
    { value: 'family', label: 'Family Member' },
    { value: 'friend', label: 'Friend' },
    { value: 'business', label: 'Business Partner' },
    { value: 'employee', label: 'Employee' },
    { value: 'contractor', label: 'Contractor' },
    { value: 'other', label: 'Other' }
  ];

  const currencyOptions = [
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'CHF', label: 'CHF - Swiss Franc' },
    { value: 'SEK', label: 'SEK - Swedish Krona' },
    { value: 'NOK', label: 'NOK - Norwegian Krone' },
    { value: 'DKK', label: 'DKK - Danish Krone' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) newErrors.name = 'Recipient name is required';
    if (!formData?.relationship) newErrors.relationship = 'Relationship is required';
    if (!formData?.bankName?.trim()) newErrors.bankName = 'Bank name is required';
    if (!formData?.iban?.trim()) newErrors.iban = 'IBAN is required';
    if (!formData?.swiftCode?.trim()) newErrors.swiftCode = 'SWIFT/BIC code is required';
    if (!formData?.country) newErrors.country = 'Country is required';
    if (!formData?.currency) newErrors.currency = 'Currency is required';
    if (!formData?.accountHolderName?.trim()) newErrors.accountHolderName = 'Account holder name is required';

    // Basic IBAN validation
    if (formData?.iban && !/^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/?.test(formData?.iban?.replace(/\s/g, ''))) {
      newErrors.iban = 'Please enter a valid IBAN format';
    }

    // Basic SWIFT validation
    if (formData?.swiftCode && !/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/?.test(formData?.swiftCode)) {
      newErrors.swiftCode = 'Please enter a valid SWIFT/BIC code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newRecipient = {
        id: Date.now()?.toString(),
        ...formData,
        verificationStatus: 'pending',
        lastUsed: new Date()?.toISOString(),
        supportedCurrencies: [formData?.currency],
        countryFlag: `https://flagcdn.com/24x18/${formData?.country?.toLowerCase()?.replace(' ', '-')}.png`
      };
      
      onAdd(newRecipient);
      onClose();
      
      // Reset form
      setFormData({
        name: '',
        relationship: '',
        bankName: '',
        iban: '',
        swiftCode: '',
        country: '',
        currency: '',
        accountHolderName: '',
        bankAddress: ''
      });
    } catch (error) {
      console.error('Error adding recipient:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Add New Recipient</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Recipient Name"
                type="text"
                placeholder="Enter full name"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                required
              />
              
              <Select
                label="Relationship"
                placeholder="Select relationship"
                options={relationshipOptions}
                value={formData?.relationship}
                onChange={(value) => handleInputChange('relationship', value)}
                error={errors?.relationship}
                required
              />
            </div>
          </div>

          {/* Banking Information */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Banking Information</h3>
            <div className="space-y-4">
              <Input
                label="Bank Name"
                type="text"
                placeholder="Enter bank name"
                value={formData?.bankName}
                onChange={(e) => handleInputChange('bankName', e?.target?.value)}
                error={errors?.bankName}
                required
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="IBAN"
                  type="text"
                  placeholder="GB29 NWBK 6016 1331 9268 19"
                  value={formData?.iban}
                  onChange={(e) => handleInputChange('iban', e?.target?.value?.toUpperCase())}
                  error={errors?.iban}
                  required
                />
                
                <Input
                  label="SWIFT/BIC Code"
                  type="text"
                  placeholder="NWBKGB2L"
                  value={formData?.swiftCode}
                  onChange={(e) => handleInputChange('swiftCode', e?.target?.value?.toUpperCase())}
                  error={errors?.swiftCode}
                  required
                />
              </div>
              
              <Input
                label="Account Holder Name"
                type="text"
                placeholder="Name as it appears on bank account"
                value={formData?.accountHolderName}
                onChange={(e) => handleInputChange('accountHolderName', e?.target?.value)}
                error={errors?.accountHolderName}
                required
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Country"
                  placeholder="Select country"
                  options={countryOptions}
                  value={formData?.country}
                  onChange={(value) => handleInputChange('country', value)}
                  error={errors?.country}
                  required
                />
                
                <Select
                  label="Currency"
                  placeholder="Select currency"
                  options={currencyOptions}
                  value={formData?.currency}
                  onChange={(value) => handleInputChange('currency', value)}
                  error={errors?.currency}
                  required
                />
              </div>
              
              <Input
                label="Bank Address (Optional)"
                type="text"
                placeholder="Enter bank address"
                value={formData?.bankAddress}
                onChange={(e) => handleInputChange('bankAddress', e?.target?.value)}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              iconName="Plus"
              iconPosition="left"
              iconSize={16}
            >
              Add Recipient
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRecipientModal;